import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContentText from '@mui/material/DialogContentText';
import MenuItem from '@mui/material/MenuItem';
import { mdiPlus } from '@mdi/js';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useApolloClient, useMutation } from '@apollo/client/react';

import { AddUsersToGroupDocument, IGroupFragment } from 'graphql/types';
import { ActionButtonDropdown } from 'common/components/ActionButtonDropdown';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { IGlobalDrawerType } from 'common/types';
import { getErrorMessage } from 'common/utils/getErrorMessage';
import { parseJSON } from 'common/utils/json';
import { FilePicker } from 'common/components/FilePicker';
import { EGroupUserRole, EUsersImportType } from 'user/types';
import { UserImportBlockedDialog } from 'user/components/UserImportBlockedDialog';
import { UserImportModal } from 'user/components/UserImportModal';
import { useUserImportGuard } from 'user/hooks/useUserImportGuard';
import { useUserImport } from 'user/hooks/useUserImport';
import { useGroupUserCount } from 'user/hooks/group/useGroupUserCount';

interface IAddUsersHandlers {
    onAddUsers: (userIds: string[]) => Promise<void>;
    loadingAddUsers: boolean;
    isSelectorOpen: boolean;
    onSelectorOpenChange: (open: boolean) => void;
}

interface IProps {
    group: IGroupFragment;
    userAmount: number;
    onUserUpdate?: () => void;
    children: (handlers: IAddUsersHandlers) => ReactNode;
}

export const AddUsersButton = ({
    group,
    userAmount,
    onUserUpdate,
    children,
}: IProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();
    const [displaySnackbar] = useSnackbar();
    const { openGlobalDrawer } = useGlobalDrawer();
    const [showMaxUsersDialog, setShowMaxUsersDialog] = useState(false);
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);

    const {
        completeImportData,
        importModalOpen,
        handleFileUpload,
        handleImportModalClose,
    } = useUserImport({
        onUploadError: (_fileName, message) => {
            displaySnackbar(message, { variant: 'error' });
        },
    });

    const { id: groupId, maxUsers, permissions } = group;

    const permissionsObj = parseJSON(permissions || '');
    const {
        canAddParticipants = false,
        canCreateUser = false,
        canImportUsers = false,
    } = permissionsObj || {};

    const {
        guardedImportClick,
        checkingImports,
        importBlockedDialogOpen,
        handleImportBlockedClose,
    } = useUserImportGuard({ canImport: canImportUsers });

    const groupIsFull = maxUsers > 0 && userAmount >= maxUsers;

    const [addUsersToGroup, { loading: loadingAddUsers }] = useMutation(
        AddUsersToGroupDocument
    );

    const { updateGroupUserCount } = useGroupUserCount({ groupId });

    const handleImportSuccess = useCallback(
        (succeededCount: number) => {
            updateGroupUserCount(succeededCount);
            client.refetchQueries({ include: ['GroupUsers'] });
        },
        [updateGroupUserCount, client]
    );

    if (!canAddParticipants && !canCreateUser && !canImportUsers) return null;

    const handleAddParticipantsToGroup = async (selectedUsers: string[]) => {
        if (maxUsers > 0 && userAmount + selectedUsers.length > maxUsers) {
            setShowMaxUsersDialog(true);

            return;
        }

        try {
            const result = await addUsersToGroup({
                variables: {
                    groupId,
                    userIds: selectedUsers,
                },
            });

            const { addedIds, failedIds } = result.data?.addUsersToGroup || {};

            if (addedIds?.length) {
                displaySnackbar(
                    translate('groupForm.participantsAddedSuccess', {
                        count: addedIds.length,
                    }),
                    { variant: 'success' }
                );

                updateGroupUserCount(addedIds.length);

                // Refetch the selector and the participants list
                client.cache.evict({ fieldName: 'paginatedUsers' });
                client.cache.gc();
                client.refetchQueries({ include: ['GroupUsers'] });

                onUserUpdate?.();
            }

            if (failedIds?.length) {
                displaySnackbar(
                    translate('groupForm.participantsAddedFailed', {
                        count: failedIds.length,
                    }),
                    { variant: 'error' }
                );
            }
        } catch (error) {
            if (!CombinedGraphQLErrors.is(error)) throw error;

            error.errors.forEach((e) => {
                const errorMessage = getErrorMessage(e);

                if (errorMessage) {
                    displaySnackbar(errorMessage);
                }
            });
        }
    };

    const handleOpenCreateDrawer = () => {
        if (groupIsFull) {
            setShowMaxUsersDialog(true);

            return;
        }

        openGlobalDrawer({
            type: IGlobalDrawerType.UserCreate,
            props: {
                groupId,
                groupUserRole: EGroupUserRole.Participant,
                onCreate: () => {
                    client.refetchQueries({
                        include: ['GroupUsers'],
                    });

                    updateGroupUserCount(1);
                },
            },
        });
    };

    const handleImportFile = useCallback(() => {
        if (groupIsFull) {
            setShowMaxUsersDialog(true);

            return;
        }

        guardedImportClick('group-users-import-csv');
    }, [groupIsFull, guardedImportClick]);

    const options = [
        ...(canAddParticipants
            ? [
                  {
                      key: 'add-users',
                      label: translate('groupForm.addUsers'),
                      onClick: () => setIsSelectorOpen(true),
                  },
              ]
            : []),
        ...(canCreateUser
            ? [
                  {
                      key: 'create-user',
                      label: translate('groupForm.createNewUser'),
                      onClick: handleOpenCreateDrawer,
                  },
              ]
            : []),
        ...(canImportUsers
            ? [
                  {
                      key: 'import-file',
                      label: translate('groupForm.importFile'),
                      onClick: handleImportFile,
                  },
              ]
            : []),
    ];

    const [primaryOption] = options;

    const menuItems =
        options.length > 1
            ? options.map((option) => (
                  <MenuItem key={option.key} onClick={option.onClick}>
                      {option.label}
                  </MenuItem>
              ))
            : undefined;

    return (
        <>
            <ActionButtonDropdown
                disabled={checkingImports}
                menuItems={menuItems}
                size="medium"
                onClick={primaryOption.onClick}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {checkingImports ? (
                        <CircularProgress color="inherit" size={20} />
                    ) : (
                        <Icon path={mdiPlus} />
                    )}
                </Box>

                {primaryOption.label}
            </ActionButtonDropdown>

            <UserImportBlockedDialog
                open={importBlockedDialogOpen}
                onClose={handleImportBlockedClose}
            />

            <AlertDialog
                actions={
                    <Button
                        variant="contained"
                        onClick={() => {
                            setShowMaxUsersDialog(false);
                        }}
                    >
                        {translate('cancel')}
                    </Button>
                }
                open={showMaxUsersDialog}
                title={translate('addUsersToGroupLimitDialog.title')}
            >
                <DialogContentText color="text.primary" variant="body2">
                    {translate('addUsersToGroupLimitDialog.text')}
                </DialogContentText>
            </AlertDialog>

            {canImportUsers && (
                <>
                    <FilePicker
                        id="group-users-import-csv"
                        inputProps={{ accept: '.csv' }}
                        sx={{
                            position: 'absolute',
                            overflow: 'hidden',
                            width: 0,
                            height: 0,
                        }}
                        onUpload={handleFileUpload}
                    />

                    <UserImportModal
                        forRelatedIds={[groupId]}
                        importData={completeImportData}
                        importType={EUsersImportType.Group}
                        open={importModalOpen}
                        onClose={handleImportModalClose}
                        onSuccess={handleImportSuccess}
                    />
                </>
            )}

            {children({
                onAddUsers: handleAddParticipantsToGroup,
                loadingAddUsers,
                isSelectorOpen,
                onSelectorOpenChange: setIsSelectorOpen,
            })}
        </>
    );
};
